import { createSlice } from "@reduxjs/toolkit";

import { CampaignRoutingSettings } from "@signalist/shared-types";

import { RootState } from "@/redux";

import { campaignRoutingApi } from "./campaignRouting.api";

/**
 * Each list's campaign routing as the server last answered it (PREC-4219), by list id. A list whose
 * read failed is in `failedListIds`, so the screens say the rules could not be read instead of
 * showing an empty rule set (a failed lookup never reads as "no rules"). A list whose save (PUT) is
 * in flight is in `savingListIds`: the editor can be closed while it saves, so the pending state
 * lives here, outside the dialog, and an editor opened meanwhile waits for the answer instead of
 * seeding a draft from the settings that save is replacing.
 */
interface CampaignRoutingState {
	byListId: Record<number, CampaignRoutingSettings>;
	failedListIds: number[];
	savingListIds: number[];
}

const initialState: CampaignRoutingState = { byListId: {}, failedListIds: [], savingListIds: [] };

const stopSaving = (state: CampaignRoutingState, listId: number) => {
	state.savingListIds = state.savingListIds.filter((id) => id !== listId);
};

const store = (state: CampaignRoutingState, settings: CampaignRoutingSettings) => {
	state.byListId[settings.listId] = settings;
	state.failedListIds = state.failedListIds.filter((id) => id !== settings.listId);
};

export const campaignRoutingSlice = createSlice({
	name: "campaignRouting",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder.addMatcher(
			campaignRoutingApi.endpoints.getCampaignRouting.matchFulfilled,
			(state, { payload }) => store(state, payload)
		);
		builder.addMatcher(
			campaignRoutingApi.endpoints.saveCampaignRouting.matchPending,
			(state, { meta }) => {
				const { listId } = meta.arg.originalArgs;

				if (!state.savingListIds.includes(listId)) {
					state.savingListIds.push(listId);
				}
			}
		);
		// The saved settings and the end of the save land in one update, so a waiting editor opens
		// on the answer of the PUT, never on the settings it replaced.
		builder.addMatcher(
			campaignRoutingApi.endpoints.saveCampaignRouting.matchFulfilled,
			(state, { payload, meta }) => {
				store(state, payload);
				stopSaving(state, meta.arg.originalArgs.listId);
			}
		);
		builder.addMatcher(
			campaignRoutingApi.endpoints.saveCampaignRouting.matchRejected,
			(state, { meta }) => stopSaving(state, meta.arg.originalArgs.listId)
		);
		builder.addMatcher(
			campaignRoutingApi.endpoints.getCampaignRouting.matchRejected,
			(state, { meta }) => {
				// A request skipped because the same one is in flight is not a failed read.
				if (meta.condition) {
					return;
				}

				const listId = meta.arg.originalArgs;

				if (!state.failedListIds.includes(listId) && !state.byListId[listId]) {
					state.failedListIds.push(listId);
				}
			}
		);
	},
});

export const campaignRoutingReducer = campaignRoutingSlice.reducer;

export const campaignRoutingSelector = (state: RootState) => state[campaignRoutingSlice.name];

/** One list's routing as the server last answered it; undefined until read. */
export const selectCampaignRoutingSettings =
	(listId: number) =>
	(state: RootState): CampaignRoutingSettings | undefined =>
		state[campaignRoutingSlice.name].byListId[listId];

/** Whether a save (PUT) of one list's routing is in flight, from any editor. */
export const selectCampaignRoutingSaving =
	(listId: number) =>
	(state: RootState): boolean =>
		state[campaignRoutingSlice.name].savingListIds.includes(listId);

/** Whether reading one list's routing failed (and nothing was read before). */
export const selectCampaignRoutingFailed =
	(listId: number) =>
	(state: RootState): boolean =>
		state[campaignRoutingSlice.name].failedListIds.includes(listId);
